import { $createLinkNode } from '@lexical/link';
import { mergeRegister } from '@lexical/utils';
import {
  $createTextNode,
  $getNodeByKey,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_LOW,
  createCommand,
  KEY_ENTER_COMMAND,
  KEY_ESCAPE_COMMAND,
  KEY_TAB_COMMAND,
  LexicalCommand,
  LexicalEditor,
  TextNode
} from 'lexical';
import { BehaviorSubject, Observable } from 'rxjs';

export interface ContentLinkPayload {
  title: string;
  url: string;
}

export interface ContentLinkMenuOpen {
  query: string;
  anchorRect: DOMRect;
}

export type ContentLinkMenuState = ContentLinkMenuOpen | 'closed';

export const INSERT_CONTENT_LINK_COMMAND: LexicalCommand<ContentLinkPayload> =
  createCommand('INSERT_CONTENT_LINK_COMMAND');

export interface ContentLinkTriggerPlugin {
  state$: Observable<ContentLinkMenuState>;
  unregister: () => void;
}

/**
 * Inline "#" trigger for linking app content, mirroring the richtext
 * editor's "@" mention plugin: typing "#" opens a filterable menu and the
 * selected target replaces the "#query" text with a titled link.
 */
export function registerContentLinkTrigger(
  editor: LexicalEditor
): ContentLinkTriggerPlugin {
  const state$ = new BehaviorSubject<ContentLinkMenuState>('closed');

  let triggerStartOffset: number | null = null;
  let triggerNodeKey: string | null = null;
  let triggerQuery = '';

  const close = () => {
    state$.next('closed');
    triggerStartOffset = null;
    triggerNodeKey = null;
    triggerQuery = '';
  };

  const isOpen = () => state$.value !== 'closed';

  // A pointer down outside both the editor and the dropdown dismisses the menu.
  // The dropdown content (tabs + rows) is teleported to <body>, outside the
  // editor's DOM, so it is matched by its own class rather than an ancestor.
  const onOutsidePointerDown = (event: MouseEvent) => {
    if (!isOpen()) return;
    const target = event.target as HTMLElement | null;
    if (!target) return;
    if (target.closest('.content-link-dropdown')) return;
    if (editor.getRootElement()?.contains(target)) return;
    editor.update(() => close());
  };
  document.addEventListener('mousedown', onOutsidePointerDown, true);

  const unregister = mergeRegister(
    editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const trigger = findTriggerAtCursor();
        if (trigger) {
          triggerStartOffset = trigger.startOffset;
          triggerNodeKey = trigger.nodeKey;
          triggerQuery = trigger.query;
          state$.next({ query: trigger.query, anchorRect: trigger.anchorRect });
        } else if (isOpen()) {
          close();
        }
      });
    }),

    editor.registerCommand(
      INSERT_CONTENT_LINK_COMMAND,
      payload => {
        if (triggerStartOffset === null || triggerNodeKey === null)
          return false;

        const nodeKey = triggerNodeKey;
        const startOffset = triggerStartOffset;
        // "#" + query — computed instead of read from the live selection,
        // which a click on the dropdown may already have moved or cleared.
        const endOffset = startOffset + 1 + triggerQuery.length;

        editor.update(() => {
          const textNode = $getNodeByKey(nodeKey);
          if (!$isTextNode(textNode)) return;

          insertContentLink(textNode, startOffset, endOffset, payload);
          close();
        });

        return true;
      },
      COMMAND_PRIORITY_LOW
    ),

    editor.registerCommand(
      KEY_ESCAPE_COMMAND,
      () => {
        if (isOpen()) {
          close();
          return true;
        }
        return false;
      },
      COMMAND_PRIORITY_LOW
    ),

    // Block Enter/Tab while the menu is open (the dropdown handles them)
    editor.registerCommand(
      KEY_ENTER_COMMAND,
      isOpen,
      COMMAND_PRIORITY_CRITICAL
    ),
    editor.registerCommand(KEY_TAB_COMMAND, isOpen, COMMAND_PRIORITY_CRITICAL),

    () => document.removeEventListener('mousedown', onOutsidePointerDown, true)
  );

  return { state$: state$.asObservable(), unregister };
}

function findTriggerAtCursor(): {
  query: string;
  anchorRect: DOMRect;
  startOffset: number;
  nodeKey: string;
} | null {
  const selection = $getSelection();
  if (!$isRangeSelection(selection) || !selection.isCollapsed()) return null;

  const anchorNode = selection.anchor.getNode();
  if (!$isTextNode(anchorNode)) return null;

  const textBeforeCursor = anchorNode
    .getTextContent()
    .slice(0, selection.anchor.offset);
  const hashIndex = textBeforeCursor.lastIndexOf('#');

  if (hashIndex === -1) return null;

  // "#" must be at start of text or preceded by whitespace
  const charBefore = textBeforeCursor[hashIndex - 1];
  if (hashIndex > 0 && charBefore !== ' ' && charBefore !== '\n') return null;

  // Query cannot contain spaces or newlines
  const query = textBeforeCursor.slice(hashIndex + 1);
  if (query.includes(' ') || query.includes('\n')) return null;

  const anchorRect = window
    .getSelection()
    ?.getRangeAt(0)
    ?.getBoundingClientRect();
  if (!anchorRect) return null;

  return {
    query,
    anchorRect,
    startOffset: hashIndex,
    nodeKey: anchorNode.getKey()
  };
}

/**
 * Replaces "#query" text with a titled link node and a trailing space.
 */
function insertContentLink(
  textNode: TextNode,
  startOffset: number,
  endOffset: number,
  payload: ContentLinkPayload
): void {
  const splitNodes = textNode.splitText(startOffset, endOffset);
  const triggerText = startOffset === 0 ? splitNodes[0] : splitNodes[1];

  const linkNode = $createLinkNode(payload.url);
  linkNode.append($createTextNode(payload.title));
  triggerText.replace(linkNode);
  const spaceNode = new TextNode(' ');
  linkNode.insertAfter(spaceNode);
  spaceNode.select(1, 1);
}
